import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ChevronRight,
  Search,
  MapPin,
  Clock,
  Heart,
  Calendar,
  Gauge,
  Fuel,
  Settings,
  BadgeCheck,
  Flame,
  Sparkles,
  SlidersHorizontal,
  X,
  Loader2,
  Inbox,
  AlertCircle,
  RefreshCw,
  ChevronDown,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useMobileMenu } from '../hooks/useMobileMenu';
import { useAds } from '../hooks/useAds';
import { useCategories } from '../hooks/useCategories';
import type { AdListItem, ListAdsParams } from '../types/ad';

// ─────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────

const PLACEHOLDER_IMAGE =
  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 900 600"><rect width="100%" height="100%" fill="%23f3f4f6"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%239ca3af" font-family="sans-serif" font-size="28">No Image</text></svg>';

const CITIES = [
  'Karachi',
  'Lahore',
  'Islamabad',
  'Rawalpindi',
  'Faisalabad',
  'Multan',
  'Peshawar',
  'Quetta',
];

const CONDITIONS: Array<{ label: string; value: string }> = [
  { label: 'Any', value: '' },
  { label: 'New', value: 'new' },
  { label: 'Used', value: 'used' },
  { label: 'Refurbished', value: 'refurbished' },
  { label: 'For Parts', value: 'for_parts' },
];

const SORT_OPTIONS: Array<{ label: string; value: NonNullable<ListAdsParams['sort_by']> }> = [
  { label: 'Newest first', value: 'newest' },
  { label: 'Price: Low to High', value: 'price_low' },
  { label: 'Price: High to Low', value: 'price_high' },
  { label: 'Most relevant', value: 'relevance' },
];

// Categories that should show vehicle-specific specs + filters.
const VEHICLE_SLUGS = new Set([
  'cars-vehicles',
  'cars',
  'motorbikes-scooters',
  'vans-buses',
  'trucks-heavy-vehicles',
  'other-vehicles',
]);

// ─────────────────────────────────────────────────────────────
// Formatters
// ─────────────────────────────────────────────────────────────

const formatPKR = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined || value === '') return 'PKR —';
  const num = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(num)) return `PKR ${value}`;
  return new Intl.NumberFormat('en-PK', {
    style: 'currency',
    currency: 'PKR',
    maximumFractionDigits: 0,
  }).format(num);
};

const formatNumber = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined || value === '') return '';
  const num = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(num)) return String(value);
  return new Intl.NumberFormat('en-PK').format(num);
};

const formatRelativeTime = (iso: string | undefined): string => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  const diffMs = Date.now() - then;
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months}mo ago`;
  const years = Math.round(months / 12);
  return `${years}y ago`;
};

const titleCase = (value: string | null | undefined): string => {
  if (!value) return '';
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

// ─────────────────────────────────────────────────────────────
// Local filter state
// ─────────────────────────────────────────────────────────────

interface LocalFilters {
  search: string;
  city: string;
  minPrice: string;
  maxPrice: string;
  condition: string;
  make: string;
  minYear: string;
  maxYear: string;
  sortBy: NonNullable<ListAdsParams['sort_by']>;
}

const DEFAULT_FILTERS: LocalFilters = {
  search: '',
  city: '',
  minPrice: '',
  maxPrice: '',
  condition: '',
  make: '',
  minYear: '',
  maxYear: '',
  sortBy: 'newest',
};

function buildApiParams(categoryId: string | undefined, filters: LocalFilters): ListAdsParams {
  const params: ListAdsParams = {
    ...(categoryId ? { category_id: categoryId } : {}),
    sort_by: filters.sortBy,
    page: 1,
    limit: 12,
  };
  if (filters.search.trim()) params.search = filters.search.trim();
  if (filters.city) params.city = filters.city;
  const min = Number(filters.minPrice);
  const max = Number(filters.maxPrice);
  if (Number.isFinite(min) && min > 0) params.min_price = min;
  if (Number.isFinite(max) && max > 0) params.max_price = max;
  if (filters.condition) params.condition = filters.condition;
  if (filters.make.trim()) params.make = filters.make.trim();
  const minY = Number(filters.minYear);
  const maxY = Number(filters.maxYear);
  if (Number.isFinite(minY) && minY > 1900) params.min_year = minY;
  if (Number.isFinite(maxY) && maxY > 1900) params.max_year = maxY;
  return params;
}

// ─────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────

export default function CategoryPage(): JSX.Element {
  const { categoryName } = useParams<{ categoryName: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { mobileOpen, toggleMobile } = useMobileMenu();

  const categorySlug = (categoryName || '').toLowerCase();
  const isVehicleCategory = VEHICLE_SLUGS.has(categorySlug);

  // Resolve category info from the live categories API.
  const { categories: apiCategories, getCategoryBySlug } = useCategories();
  const category = useMemo(
    () => (categorySlug ? getCategoryBySlug(categorySlug) : undefined),
    [categorySlug, getCategoryBySlug]
  );

  // Sub-categories (children) of this category — for the quick-nav pills.
  const subCategories = useMemo(() => {
    if (!category) return [];
    return category.subcategories ?? [];
  }, [category]);

  // Filters — seed `search` from URL `?q=...` so Hero/search can deep-link.
  const initialSearch = searchParams.get('q') || '';
  const [filters, setFilters] = useState<LocalFilters>({
    ...DEFAULT_FILTERS,
    search: initialSearch,
  });
  const [draftFilters, setDraftFilters] = useState<LocalFilters>(filters);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Ads
  const { ads, totalAds, isLoading, isLoadingMore, error, hasMore, fetchAds, loadMore } = useAds();

  // Re-fetch whenever category slug or applied filters change.
  const lastKey = useRef<string>('');
  useEffect(() => {
    // Require a resolved category UUID before querying the API, since backend expects UUID.
    if (!category?.id) return;
    const params = buildApiParams(category.id, filters);
    const key = JSON.stringify(params);
    if (lastKey.current === key) return;
    lastKey.current = key;
    void fetchAds(params);
  }, [category?.id, filters, fetchAds]);

  const applyFilters = (next: LocalFilters): void => {
    setFilters(next);
    setMobileFiltersOpen(false);
    if (next.search) {
      searchParams.set('q', next.search);
    } else {
      searchParams.delete('q');
    }
    setSearchParams(searchParams, { replace: true });
  };

  const resetFilters = (): void => {
    setDraftFilters(DEFAULT_FILTERS);
    applyFilters(DEFAULT_FILTERS);
  };

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.search) count += 1;
    if (filters.city) count += 1;
    if (filters.minPrice) count += 1;
    if (filters.maxPrice) count += 1;
    if (filters.condition) count += 1;
    if (filters.make) count += 1;
    if (filters.minYear) count += 1;
    if (filters.maxYear) count += 1;
    return count;
  }, [filters]);

  const headerTitle = category?.name || titleCase(categorySlug) || 'Category';
  const headerParent = category?.parent_name || null;

  // ── Unknown-category fallback ────────────────────────────
  if (categoryName && apiCategories.length > 0 && !category) {
    return (
      <div className="min-h-screen bg-surface">
        <Navbar mobileOpen={mobileOpen} onToggleMobile={toggleMobile} />
        <main className="max-w-4xl mx-auto px-4 py-20 text-center">
          <Inbox className="w-16 h-16 text-dark-300 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-dark-900 mb-3">Category not found</h1>
          <p className="text-dark-600 mb-8">
            The category &ldquo;{categoryName}&rdquo; doesn&rsquo;t exist. Browse all categories to
            explore what&rsquo;s available.
          </p>
          <button
            onClick={() => navigate('/categories')}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary-600 hover:bg-primary-700 text-white font-bold transition-all"
          >
            Browse all categories
          </button>
        </main>
        <Footer onOpenPrivacySettings={() => {}} />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{headerTitle} in Pakistan · BazaarPk</title>
        <meta
          name="description"
          content={`Browse ${headerTitle} listings across Pakistan. Filter by price, make, year, condition, and location.`}
        />
      </Helmet>

      <div className="min-h-screen bg-surface">
        <Navbar mobileOpen={mobileOpen} onToggleMobile={toggleMobile} />

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
          {/* Breadcrumb */}
          <nav
            aria-label="Breadcrumb"
            className="flex items-center gap-2 text-sm font-medium flex-wrap mb-4"
          >
            <Link to="/" className="text-dark-500 hover:text-primary-600 transition-colors">
              Home
            </Link>
            <ChevronRight className="w-4 h-4 text-dark-300" />
            <Link
              to="/categories"
              className="text-dark-500 hover:text-primary-600 transition-colors"
            >
              Categories
            </Link>
            {headerParent && (
              <>
                <ChevronRight className="w-4 h-4 text-dark-300" />
                <span className="text-dark-500">{headerParent}</span>
              </>
            )}
            <ChevronRight className="w-4 h-4 text-dark-300" />
            <span className="text-primary-600 font-semibold">{headerTitle}</span>
          </nav>

          {/* Header */}
          <header className="mb-6">
            <h1 className="text-3xl sm:text-4xl font-black text-dark-900 tracking-tight">
              {headerTitle}
            </h1>
            <p className="mt-2 text-dark-600">
              {isLoading && ads.length === 0 ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading listings…
                </span>
              ) : (
                <>
                  <span className="font-bold text-dark-900">{formatNumber(totalAds)}</span>{' '}
                  {totalAds === 1 ? 'result' : 'results'}
                  {filters.city ? ` in ${filters.city}` : ''}
                  {filters.search ? ` for “${filters.search}”` : ''}
                </>
              )}
            </p>

            {/* Sub-category quick pills */}
            {subCategories.length > 0 && (
              <div
                className="mt-5 flex gap-2 overflow-x-auto pb-1 -mx-1 px-1"
                role="navigation"
                aria-label="Sub-categories"
              >
                {subCategories.map((sub) => (
                  <Link
                    key={sub.id}
                    to={`/category/${sub.slug}`}
                    className="whitespace-nowrap px-4 py-2 rounded-full bg-white border border-dark-200 text-sm font-semibold text-dark-700 hover:border-primary-300 hover:text-primary-600 hover:bg-primary-50/50 transition-all"
                  >
                    {sub.name}
                  </Link>
                ))}
              </div>
            )}
          </header>

          {/* Mobile filter trigger */}
          <div className="lg:hidden mb-4 flex items-center gap-2">
            <button
              onClick={() => {
                setDraftFilters(filters);
                setMobileFiltersOpen(true);
              }}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-dark-200 text-sm font-bold text-dark-800 hover:border-primary-300 transition-all"
            >
              <SlidersHorizontal className="w-4 h-4" />
              Filters
              {activeFilterCount > 0 && (
                <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-primary-600 text-white text-[11px] font-bold">
                  {activeFilterCount}
                </span>
              )}
            </button>
            <SortDropdown
              value={filters.sortBy}
              onChange={(v) => applyFilters({ ...filters, sortBy: v })}
            />
          </div>

          {/* Body: sidebar + results */}
          <div className="grid lg:grid-cols-[300px_1fr] gap-6 lg:gap-8 items-start">
            {/* Desktop sidebar */}
            <aside className="hidden lg:block lg:sticky lg:top-24">
              <FilterPanel
                draft={draftFilters}
                onDraftChange={setDraftFilters}
                onApply={() => applyFilters(draftFilters)}
                onReset={resetFilters}
                showVehicleFields={isVehicleCategory}
                activeCount={activeFilterCount}
              />
            </aside>

            {/* Results column */}
            <section aria-label="Listings" className="min-w-0">
              {/* Desktop sort bar */}
              <div className="hidden lg:flex items-center justify-between mb-4 bg-white border border-dark-200 rounded-xl px-4 py-3">
                <p className="text-sm text-dark-600">
                  Showing{' '}
                  <span className="font-bold text-dark-900">{formatNumber(ads.length)}</span> of{' '}
                  <span className="font-bold text-dark-900">{formatNumber(totalAds)}</span>
                </p>
                <SortDropdown
                  value={filters.sortBy}
                  onChange={(v) => applyFilters({ ...filters, sortBy: v })}
                />
              </div>

              {error ? (
                <ErrorState
                  message={error}
                  onRetry={() => fetchAds(buildApiParams(category?.id, filters))}
                />
              ) : isLoading && ads.length === 0 ? (
                <ListingSkeletons />
              ) : ads.length === 0 ? (
                <EmptyState onReset={resetFilters} hasFilters={activeFilterCount > 0} />
              ) : (
                <>
                  <ul className="space-y-4 list-none p-0 m-0">
                    {ads.map((ad) => (
                      <li key={ad.id}>
                        <ListingRow ad={ad} isVehicle={isVehicleCategory || !!ad.is_vehicle} />
                      </li>
                    ))}
                  </ul>

                  {hasMore && (
                    <div className="mt-8 flex justify-center">
                      <button
                        onClick={() => void loadMore()}
                        disabled={isLoadingMore}
                        className="inline-flex items-center gap-2 px-8 py-3 rounded-xl bg-white border-2 border-primary-200 text-primary-700 font-bold hover:bg-primary-50 hover:border-primary-300 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                      >
                        {isLoadingMore ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Loading…
                          </>
                        ) : (
                          <>Load more listings</>
                        )}
                      </button>
                    </div>
                  )}
                </>
              )}
            </section>
          </div>
        </main>

        {/* Mobile filter drawer */}
        {mobileFiltersOpen && (
          <div
            className="lg:hidden fixed inset-0 z-[60] bg-dark-900/50 backdrop-blur-sm"
            onClick={() => setMobileFiltersOpen(false)}
            role="dialog"
            aria-modal="true"
            aria-label="Filters"
          >
            <div
              className="absolute inset-y-0 left-0 w-[88%] max-w-sm bg-white shadow-2xl overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white border-b border-dark-100 px-5 py-4 flex items-center justify-between z-10">
                <h2 className="font-black text-lg text-dark-900">Filters</h2>
                <button
                  onClick={() => setMobileFiltersOpen(false)}
                  aria-label="Close filters"
                  className="p-2 rounded-lg hover:bg-dark-50 transition-colors"
                >
                  <X className="w-5 h-5 text-dark-600" />
                </button>
              </div>
              <div className="p-5">
                <FilterPanel
                  draft={draftFilters}
                  onDraftChange={setDraftFilters}
                  onApply={() => applyFilters(draftFilters)}
                  onReset={resetFilters}
                  showVehicleFields={isVehicleCategory}
                  activeCount={activeFilterCount}
                  compact
                />
              </div>
            </div>
          </div>
        )}

        <Footer onOpenPrivacySettings={() => {}} />
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// FilterPanel
// ─────────────────────────────────────────────────────────────

interface FilterPanelProps {
  draft: LocalFilters;
  onDraftChange: (next: LocalFilters) => void;
  onApply: () => void;
  onReset: () => void;
  showVehicleFields: boolean;
  activeCount: number;
  compact?: boolean;
}

function FilterPanel({
  draft,
  onDraftChange,
  onApply,
  onReset,
  showVehicleFields,
  activeCount,
  compact = false,
}: FilterPanelProps): JSX.Element {
  const update = <K extends keyof LocalFilters>(key: K, value: LocalFilters[K]) => {
    onDraftChange({ ...draft, [key]: value });
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onApply();
      }}
      className={
        compact
          ? 'space-y-5'
          : 'bg-white border border-dark-200 rounded-2xl p-5 space-y-5 shadow-sm'
      }
    >
      {!compact && (
        <div className="flex items-center justify-between pb-3 border-b border-dark-100">
          <h2 className="font-black text-dark-900 inline-flex items-center gap-2">
            <SlidersHorizontal className="w-4 h-4 text-primary-600" />
            Refine results
          </h2>
          {activeCount > 0 && (
            <button
              type="button"
              onClick={onReset}
              className="text-xs font-bold text-primary-600 hover:text-primary-700"
            >
              Clear all
            </button>
          )}
        </div>
      )}

      <FilterGroup label="Keyword">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-400" />
          <input
            type="search"
            aria-label="Keyword"
            value={draft.search}
            onChange={(e) => update('search', e.target.value)}
            placeholder="e.g. Toyota Corolla"
            className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-dark-200 bg-white text-sm text-dark-800 placeholder-dark-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none transition-all"
          />
        </div>
      </FilterGroup>

      <FilterGroup label="Location">
        <div className="relative">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-400" />
          <select
            aria-label="Location"
            value={draft.city}
            onChange={(e) => update('city', e.target.value)}
            className="w-full pl-9 pr-8 py-2.5 rounded-lg border border-dark-200 bg-white text-sm text-dark-800 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none appearance-none transition-all"
          >
            <option value="">All of Pakistan</option>
            {CITIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-400" />
        </div>
      </FilterGroup>

      <FilterGroup label="Price (PKR)">
        <div className="grid grid-cols-2 gap-2">
          <input
            type="number"
            aria-label="Minimum price in Pakistani rupees"
            inputMode="numeric"
            min={0}
            value={draft.minPrice}
            onChange={(e) => update('minPrice', e.target.value)}
            placeholder="Min"
            className="w-full px-3 py-2.5 rounded-lg border border-dark-200 bg-white text-sm text-dark-800 placeholder-dark-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none transition-all"
          />
          <input
            type="number"
            aria-label="Maximum price in Pakistani rupees"
            inputMode="numeric"
            min={0}
            value={draft.maxPrice}
            onChange={(e) => update('maxPrice', e.target.value)}
            placeholder="Max"
            className="w-full px-3 py-2.5 rounded-lg border border-dark-200 bg-white text-sm text-dark-800 placeholder-dark-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none transition-all"
          />
        </div>
      </FilterGroup>

      <FilterGroup label="Condition">
        <div className="flex flex-wrap gap-2">
          {CONDITIONS.map((c) => {
            const active = draft.condition === c.value;
            return (
              <button
                key={c.label}
                type="button"
                onClick={() => update('condition', c.value)}
                aria-pressed={active}
                className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-all ${
                  active
                    ? 'bg-primary-600 border-primary-600 text-white'
                    : 'bg-white border-dark-200 text-dark-700 hover:border-primary-300 hover:text-primary-600'
                }`}
              >
                {c.label}
              </button>
            );
          })}
        </div>
      </FilterGroup>

      {showVehicleFields && (
        <>
          <FilterGroup label="Make">
            <input
              type="text"
              aria-label="Vehicle make"
              value={draft.make}
              onChange={(e) => update('make', e.target.value)}
              placeholder="e.g. Toyota, Honda"
              className="w-full px-3 py-2.5 rounded-lg border border-dark-200 bg-white text-sm text-dark-800 placeholder-dark-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none transition-all"
            />
          </FilterGroup>

          <FilterGroup label="Year">
            <div className="grid grid-cols-2 gap-2">
              <input
                type="number"
                aria-label="Minimum vehicle year"
                inputMode="numeric"
                min={1950}
                max={new Date().getFullYear() + 1}
                value={draft.minYear}
                onChange={(e) => update('minYear', e.target.value)}
                placeholder="From"
                className="w-full px-3 py-2.5 rounded-lg border border-dark-200 bg-white text-sm text-dark-800 placeholder-dark-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none transition-all"
              />
              <input
                type="number"
                aria-label="Maximum vehicle year"
                inputMode="numeric"
                min={1950}
                max={new Date().getFullYear() + 1}
                value={draft.maxYear}
                onChange={(e) => update('maxYear', e.target.value)}
                placeholder="To"
                className="w-full px-3 py-2.5 rounded-lg border border-dark-200 bg-white text-sm text-dark-800 placeholder-dark-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none transition-all"
              />
            </div>
          </FilterGroup>
        </>
      )}

      <div className="flex gap-2 pt-2">
        <button
          type="submit"
          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-sm font-bold shadow-sm shadow-primary-500/20 transition-all"
        >
          Apply filters
        </button>
        {activeCount > 0 && (
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center justify-center px-4 py-3 rounded-xl bg-dark-50 hover:bg-dark-100 text-dark-700 text-sm font-bold transition-all"
            aria-label="Clear all filters"
          >
            Clear
          </button>
        )}
      </div>
    </form>
  );
}

function FilterGroup({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <fieldset>
      <legend className="text-[11px] font-black text-dark-500 uppercase tracking-wider mb-2">{label}</legend>
      {children}
    </fieldset>
  );
}

// ─────────────────────────────────────────────────────────────
// SortDropdown
// ─────────────────────────────────────────────────────────────

function SortDropdown({
  value,
  onChange,
}: {
  value: NonNullable<ListAdsParams['sort_by']>;
  onChange: (v: NonNullable<ListAdsParams['sort_by']>) => void;
}): JSX.Element {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as NonNullable<ListAdsParams['sort_by']>)}
        className="appearance-none pl-3 pr-9 py-2.5 rounded-lg border border-dark-200 bg-white text-sm font-semibold text-dark-800 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none transition-all"
        aria-label="Sort listings"
      >
        {SORT_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-400" />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ListingRow (Gumtree-style horizontal card)
// ─────────────────────────────────────────────────────────────

function ListingRow({ ad, isVehicle }: { ad: AdListItem; isVehicle: boolean }): JSX.Element {
  const navigate = useNavigate();
  const [isSaved, setIsSaved] = useState(false);

  const img = ad.primary_image || ad.images?.[0] || PLACEHOLDER_IMAGE;
  const location = [ad.location, ad.city].filter(Boolean).join(', ') || '—';
  const posted = formatRelativeTime(ad.created_at);
  const showVehicleSpecs = isVehicle && (ad.year || ad.mileage || ad.fuel_type || ad.transmission);

  const handleClick = () => navigate(`/item/${ad.id}`);
  const handleSave = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsSaved((v) => !v);
  };

  return (
    <article
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter') handleClick();
      }}
      role="link"
      tabIndex={0}
      className="group bg-white rounded-2xl border border-dark-200 overflow-hidden shadow-sm hover:shadow-lg hover:border-primary-200 transition-all duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-400"
    >
      <div className="flex flex-col sm:flex-row">
        {/* Image */}
        <div className="relative sm:w-[260px] sm:min-w-[260px] h-56 sm:h-48 bg-dark-50 overflow-hidden">
          <img
            src={img}
            alt={ad.title}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).src = PLACEHOLDER_IMAGE;
            }}
          />
          <div className="absolute top-3 left-3 flex gap-1.5 flex-wrap">
            {ad.is_urgent && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-danger text-white text-[10px] font-black uppercase tracking-wide shadow-sm">
                <Flame className="w-3 h-3" />
                Urgent
              </span>
            )}
            {ad.is_featured && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-warning text-dark-900 text-[10px] font-black uppercase tracking-wide shadow-sm">
                <Sparkles className="w-3 h-3" />
                Featured
              </span>
            )}
            {ad.condition === 'new' && !ad.is_urgent && !ad.is_featured && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-success text-white text-[10px] font-black uppercase tracking-wide shadow-sm">
                <BadgeCheck className="w-3 h-3" />
                New
              </span>
            )}
          </div>
          {Array.isArray(ad.images) && ad.images.length > 1 && (
            <span className="absolute bottom-3 left-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-dark-900/70 backdrop-blur-sm text-white text-[11px] font-bold">
              {ad.images.length} photos
            </span>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 min-w-0 p-4 sm:p-5 flex flex-col">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h3 className="text-base sm:text-lg font-bold text-dark-900 leading-tight line-clamp-2 group-hover:text-primary-600 transition-colors">
                {ad.title}
              </h3>
              <p className="mt-1 text-xs text-dark-500 truncate">
                {ad.category_path || ad.category_name}
              </p>
            </div>
            <button
              onClick={handleSave}
              aria-label={isSaved ? 'Remove from saved' : 'Save listing'}
              aria-pressed={isSaved}
              className={`p-2 rounded-full border transition-all flex-shrink-0 ${
                isSaved
                  ? 'bg-danger/10 border-danger/20 text-danger'
                  : 'bg-white border-dark-200 text-dark-400 hover:text-danger hover:border-danger/30'
              }`}
            >
              <Heart className={`w-4 h-4 ${isSaved ? 'fill-danger' : ''}`} />
            </button>
          </div>

          <div className="mt-2 flex items-baseline gap-2">
            <p className="text-xl sm:text-2xl font-black text-primary-600 tracking-tight">
              {formatPKR(ad.price)}
            </p>
            {ad.is_price_negotiable && (
              <span className="text-[10px] font-black text-success uppercase tracking-wider">
                Negotiable
              </span>
            )}
          </div>

          {showVehicleSpecs && (
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-dark-600">
              {ad.year && (
                <span className="inline-flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5 text-dark-400" />
                  {ad.year}
                </span>
              )}
              {ad.mileage !== null && ad.mileage !== undefined && (
                <span className="inline-flex items-center gap-1.5">
                  <Gauge className="w-3.5 h-3.5 text-dark-400" />
                  {formatNumber(ad.mileage)} km
                </span>
              )}
              {ad.fuel_type && (
                <span className="inline-flex items-center gap-1.5">
                  <Fuel className="w-3.5 h-3.5 text-dark-400" />
                  {titleCase(ad.fuel_type)}
                </span>
              )}
              {ad.transmission && (
                <span className="inline-flex items-center gap-1.5">
                  <Settings className="w-3.5 h-3.5 text-dark-400" />
                  {titleCase(ad.transmission)}
                </span>
              )}
            </div>
          )}

          {!showVehicleSpecs && (ad.short_description || ad.description) && (
            <p className="mt-2 text-sm text-dark-600 line-clamp-2">
              {ad.short_description || ad.description}
            </p>
          )}

          <div className="mt-auto pt-4 flex items-center justify-between gap-3 text-xs text-dark-500">
            <span className="inline-flex items-center gap-1.5 min-w-0">
              <MapPin className="w-3.5 h-3.5 text-primary-500 flex-shrink-0" />
              <span className="truncate">{location}</span>
            </span>
            {posted && (
              <span className="inline-flex items-center gap-1.5 flex-shrink-0">
                <Clock className="w-3.5 h-3.5" />
                {posted}
              </span>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

// ─────────────────────────────────────────────────────────────
// States: loading / empty / error
// ─────────────────────────────────────────────────────────────

function ListingSkeletons(): JSX.Element {
  return (
    <ul className="space-y-4 list-none p-0 m-0">
      {Array.from({ length: 4 }).map((_, i) => (
        <li
          key={i}
          className="bg-white rounded-2xl border border-dark-200 overflow-hidden flex flex-col sm:flex-row animate-pulse"
        >
          <div className="sm:w-[260px] sm:min-w-[260px] h-48 bg-dark-100" />
          <div className="flex-1 p-5 space-y-3">
            <div className="h-5 bg-dark-100 rounded w-3/4" />
            <div className="h-4 bg-dark-100 rounded w-1/3" />
            <div className="h-7 bg-dark-100 rounded w-1/2" />
            <div className="flex gap-3">
              <div className="h-3 bg-dark-100 rounded w-16" />
              <div className="h-3 bg-dark-100 rounded w-16" />
              <div className="h-3 bg-dark-100 rounded w-16" />
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

function EmptyState({
  onReset,
  hasFilters,
}: {
  onReset: () => void;
  hasFilters: boolean;
}): JSX.Element {
  return (
    <div className="bg-white border border-dark-200 rounded-2xl p-12 text-center">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-dark-50 mb-4">
        <Inbox className="w-8 h-8 text-dark-400" />
      </div>
      <h3 className="text-xl font-black text-dark-900 mb-2">No listings found</h3>
      <p className="text-dark-600 max-w-md mx-auto mb-6">
        {hasFilters
          ? 'Try widening your search by removing some filters or expanding your price range.'
          : 'There are no listings in this category yet. Check back soon or browse other categories.'}
      </p>
      {hasFilters && (
        <button
          onClick={onReset}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary-600 hover:bg-primary-700 text-white font-bold transition-all"
        >
          Clear all filters
        </button>
      )}
    </div>
  );
}

function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}): JSX.Element {
  return (
    <div className="bg-white border border-danger/20 rounded-2xl p-10 text-center">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-danger/10 mb-4">
        <AlertCircle className="w-8 h-8 text-danger" />
      </div>
      <h3 className="text-xl font-black text-dark-900 mb-2">Couldn&rsquo;t load listings</h3>
      <p className="text-dark-600 max-w-md mx-auto mb-6">{message}</p>
      <button
        onClick={onRetry}
        className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white border-2 border-dark-200 text-dark-800 font-bold hover:border-primary-300 hover:text-primary-600 transition-all"
      >
        <RefreshCw className="w-4 h-4" />
        Try again
      </button>
    </div>
  );
}
